import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import isNil from 'lodash/isNil';
import { ReactNode } from 'react';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import { ICON_SIZE } from '@/components/organisms/PageHeader/constants';

type PageHeaderDescriptionProps = {
  /**
   * Description body content. A plain string is wrapped in body2 typography
   * with pre-wrap whitespace and break-word wrapping; other nodes render
   * as-is. When the value is nullish or a whitespace-only string, the slot
   * renders nothing — so call sites can pass `building.description` without
   * an external guard.
   */
  children: ReactNode;
  /**
   * Visible label on the toggle row (e.g. "Description"). Also used as the
   * `aria-label` for the expand button.
   */
  label: string;
  /**
   * Initial expanded state. Defaults to `false` so the description does not
   * push other page content down on every load. See ADR-011.
   */
  defaultExpanded?: boolean;
};

const isEmptyContent = (children: ReactNode): boolean => {
  if (isNil(children) || children === false) return true;
  if (typeof children === 'string') return children.trim().length === 0;
  return false;
};

export const PageHeaderDescription = ({ children, label, defaultExpanded = false }: PageHeaderDescriptionProps) => {
  if (isEmptyContent(children)) return null;

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      square
      sx={{
        bgcolor: 'transparent',
        backgroundImage: 'none',
        mt: 1,
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { boxShadow: 'none', m: 0, mt: 1, bgcolor: 'transparent' },
        '& .MuiAccordionDetails-root': { bgcolor: 'transparent' },
        '& .MuiAccordionSummary-root': { bgcolor: 'transparent' },
      }}
    >
      <AccordionSummary
        aria-label={label}
        expandIcon={
          <Iconify icon={ICONS.CHEVRON_DOWN} width={ICON_SIZE} height={ICON_SIZE} sx={{ color: 'text.secondary' }} />
        }
        sx={{
          px: 0,
          py: 0.75,
          minHeight: 0,
          flexDirection: 'row-reverse',
          gap: 0.5,
          '& .MuiAccordionSummary-content': { m: 0 },
          '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'rotate(-90deg)',
            '&.Mui-expanded': { transform: 'rotate(0deg)' },
          },
          '&.Mui-expanded': { minHeight: 0, py: 0.75 },
        }}
      >
        <PTypography variant="caption" color="text.secondary" sx={{ mt: '2px' }}>
          {label}
        </PTypography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 1, pl: 2.5 }}>
        {typeof children === 'string' ? (
          <PTypography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {children}
          </PTypography>
        ) : (
          children
        )}
      </AccordionDetails>
    </Accordion>
  );
};
